'use client';

import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { FileText } from 'lucide-react';
import {
  buildListHeaderFromListBody,
  DEFAULT_FARE_RULE_ERROR_MESSAGE,
  FareRuleListHeader,
  FormattedFareRuleData,
  formatFareRuleLoading,
  formatFetchFareRuleResponse,
  isFareRuleArrayObjectFormat,
  resolveActiveFareRuleTab,
} from '@/lib/fare-rule-formatter';

interface FareRuleProps {
  fareRules?: unknown;
  isLoader?: boolean;
}

const sanitizeHtml = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return '';

  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'b',
      'i',
      'em',
      'strong',
      'span',
      'ul',
      'ol',
      'li',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'div',
    ],
    ALLOWED_ATTR: ['class', 'style'],
  });
};

const toText = (value: unknown) => {
  if (value == null || value === '') return '-';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const title = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isFormattedFareRule = (value: unknown): value is FormattedFareRuleData => {
  if (!value || typeof value !== 'object') return false;
  const data = value as FormattedFareRuleData;
  return !!(
    data.success !== undefined ||
    data.isLoader !== undefined ||
    data.list_body !== undefined ||
    data.arrayData !== undefined ||
    data.fare_rule !== undefined ||
    data.body !== undefined ||
    data.html !== undefined ||
    data.rules !== undefined ||
    data.message !== undefined
  );
};

const normalizeFareRule = (fareRules: unknown, isLoader?: boolean) => {
  if (isLoader) return formatFareRuleLoading();
  if (isFormattedFareRule(fareRules)) return fareRules;
  return formatFetchFareRuleResponse(fareRules);
};

const Loader = () => (
  <div className="space-y-3 p-2">
    {[0, 1, 2].map((item) => (
      <div key={item} className="h-5 animate-pulse rounded bg-gray-200" />

  //     <div className="m-5 flex flex-col items-center space-y-4">
  //   <Skeleton className="h-5 w-[40%]" />
  //   <Skeleton className="h-5 w-[60%]" />
  //   <Skeleton className="h-5 w-[40%]" />
  //   <div className="pt-2" />
  //   <Skeleton className="h-5 w-[40%]" />
  //   <Skeleton className="h-5 w-[60%]" />
  //   <Skeleton className="h-5 w-[40%]" />
  // </div>
    ))}
  </div>
);

const Empty = ({ message }: { message?: string }) => (
  <div className="flex flex-col items-center justify-center py-6 text-center md:py-8">
    <FileText className="h-8 w-8 text-gray-600 md:h-10 md:w-10" />
    <p className="mt-2 text-xs text-gray-600 md:text-sm">
      {message || DEFAULT_FARE_RULE_ERROR_MESSAGE}
    </p>
  </div>
);

const KeyValue = ({ data }: { data?: Record<string, unknown> }) => {
  const entries = Object.entries(data || {});
  if (!entries.length) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-[11px] md:text-xs">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key}>
              <th className="w-1/2 border border-gray-200 bg-gray-50 p-2 font-semibold text-gray-800">
                {title(key)}
              </th>
              <td className="border border-gray-200 p-2 text-gray-700">{toText(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const HtmlBlock = ({ value }: { value: unknown }) => {
  const html = typeof value === 'string' ? value : (value as any)?.fareRule;
  const sanitizedHtml = sanitizeHtml(html);

  if (!sanitizedHtml) return null;

  return (
    <article
      className="prose prose-sm max-w-none overflow-x-auto text-[11px] text-gray-700 md:text-xs [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:p-1 md:[&_td]:p-2 [&_th]:border [&_th]:border-gray-200 [&_th]:p-1 md:[&_th]:p-2"
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

const RuleRows = ({ rows }: { rows?: any[] }) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <div className="space-y-2">
      {rows.map((row, index) => (
        <div key={`${row?.time || 'row'}-${index}`} className="rounded-md border border-gray-200 p-2 md:p-3">
          <div className="mb-2 text-[11px] font-semibold text-gray-800 md:text-xs">
            {row?.departureType ? `${title(String(row.departureType))} - ` : ''}
            {toText(row?.time)}
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {(Array.isArray(row?.content) ? row.content : []).map((item: any, itemIndex: number) => (
              <div key={`${item?.head || 'item'}-${itemIndex}`} className="rounded border border-gray-200 p-2">
                {item?.head ? (
                  <div className="text-[11px] font-semibold text-gray-800 md:text-xs">
                    {toText(item.head)}
                  </div>
                ) : null}
                <div className="text-[11px] text-gray-700 md:text-xs">{toText(item?.value)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const normalizeTime = (value: unknown): string =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : toText(value);

const ruleCellLines = (rule: any): string[] => {
  const content = Array.isArray(rule?.content) ? rule.content : [];
  const lines = content.flatMap((item: any) => {
    const head = item?.head != null ? toText(item.head).trim() : '';
    const value = item?.value;
    const values = Array.isArray(value) ? value : [value];
    const valueLines = values
      .map((entry) => (entry != null ? toText(entry).trim() : ''))
      .filter(Boolean);

    return [head, ...valueLines].filter(Boolean);
  });

  return lines;
};

const ListBody = ({ data }: { data: FormattedFareRuleData }) => {
  const listBody = data.list_body;
  const body = !Array.isArray(listBody) && listBody && typeof listBody === 'object' ? listBody : null;
  const headers: FareRuleListHeader[] = body
    ? data.list_header?.length
      ? data.list_header
      : buildListHeaderFromListBody(body)
    : [];
  const visibleHeaders = headers.filter(
    (header) => Array.isArray(body?.[header.template]) && body[header.template].length > 0,
  );
  const defaultTab = body
    ? data.activeFareRuleTab || resolveActiveFareRuleTab({ list_header: visibleHeaders, list_body: body })
    : '';
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  if (!body || visibleHeaders.length === 0) return null;

  const activeHeader = visibleHeaders.find((header) => header.template === activeTab) || visibleHeaders[0];
  const activeRows = Array.isArray(body[activeHeader.template]) ? body[activeHeader.template] : [];
  const timeFrames = activeRows
    .map((row: any) => normalizeTime(row?.time))
    .filter((time: string) => time && time !== '-');

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-300 bg-white">
      <table className="min-w-[520px] w-full border-collapse text-center text-[11px] text-gray-900 md:text-xs">
        <thead>
          <tr className="border-b border-gray-300">
            <th className="w-[22%] px-3 py-3 font-semibold">Time frame</th>
            <th className="px-0 py-0 font-semibold">
              <div className="flex gap-2 overflow-x-auto px-2 py-2">
                {visibleHeaders.map((header) => (
                  <button
                    key={header.template}
                    type="button"
                    onClick={() => setActiveTab(header.template)}
                    className={`min-w-[150px] whitespace-nowrap rounded-lg px-3 py-2 text-[11px] font-bold transition-colors md:text-xs ${
                      activeTab === header.template
                        ? 'bg-[#0f3d70] text-white'
                        : 'bg-transparent text-gray-950 hover:bg-gray-100'
                    }`}
                  >
                    {header.header_name}
                  </button>
                ))}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {timeFrames.map((time) => {
            const rule = activeRows.find((row: any) => normalizeTime(row?.time) === time);
            const lines = rule ? ruleCellLines(rule) : [];

            return (
              <tr key={time} className="border-b border-gray-300 last:border-b-0">
                <th className="border-r border-gray-300 px-3 py-4 font-medium text-gray-900">
                  {time}
                </th>
                <td className="px-3 py-4 text-left align-top font-semibold text-gray-950">
                  <div className="grid grid-cols-1 gap-2">
                    {lines.map((line, index) => (
                      <div key={`${line}-${index}`}>
                        {line}
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ArrayObjectBody = ({ fareRule }: { fareRule: any }) => {
  const isArrayObject = isFareRuleArrayObjectFormat(fareRule);
  const body = isArrayObject ? (fareRule[0] as Record<string, any[]>) : {};
  const tabs = Object.keys(body).filter((key) => Array.isArray(body[key]) && body[key].length > 0);
  const [activeTab, setActiveTab] = useState(tabs[0] || '');

  useEffect(() => {
    setActiveTab(tabs[0] || '');
  }, [tabs[0]]);

  if (!isArrayObject || !tabs.length) return null;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`whitespace-nowrap rounded-md border px-3 py-1 text-[11px] font-medium md:text-xs ${
              activeTab === tab
                ? 'border-primary bg-primary text-white'
                : 'border-gray-200 bg-white text-gray-700'
            }`}
          >
            {title(tab)}
          </button>
        ))}
      </div>
      <RuleRows rows={body[activeTab]} />
    </div>
  );
};

const RulesList = ({ rules }: { rules?: any[] }) => {
  if (!Array.isArray(rules) || rules.length === 0) return null;

  return (
    <div className="space-y-3">
      {rules.map((rule, index) => (
        <div key={`${rule?.type || 'rule'}-${index}`} className="rounded-md border border-gray-200 p-3">
          <div className="mb-2 text-[11px] font-semibold text-gray-800 md:text-xs">
            {[rule?.type, rule?.category].filter(Boolean).join(' ')}
          </div>
          <HtmlBlock value={rule?.content} />
        </div>
      ))}
    </div>
  );
};

const BodyBlock = ({ body }: { body: FormattedFareRuleData['body'] }) => {
  if (!body) return null;

  if (Array.isArray(body)) {
    return (
      <div className="space-y-3">
        {body.map((item, index) => (
          <div key={index} className="rounded-md border border-gray-200 p-3">
            <KeyValue data={item} />
          </div>
        ))}
      </div>
    );
  }

  return <KeyValue data={body} />;
};

const FareRule = ({ fareRules, isLoader }: FareRuleProps) => {
  const data = useMemo(() => normalizeFareRule(fareRules, isLoader), [fareRules, isLoader]);
  const htmlData = data.html?.data;
  const rules = data.rules || (Array.isArray(htmlData?.rules) ? htmlData.rules : undefined);

  let content: React.ReactNode = null;

  if (data.isLoader) {
    content = <Loader />;
  } else if (data.list_body && !Array.isArray(data.list_body)) {
    content = <ListBody data={data} />;
  } else if (isFareRuleArrayObjectFormat(data.fare_rule)) {
    content = <ArrayObjectBody fareRule={data.fare_rule} />;
  } else if (rules?.length) {
    content = <RulesList rules={rules} />;
  } else if (htmlData) {
    content = <HtmlBlock value={htmlData} />;
  } else if (data.fare_rule) {
    content = <KeyValue data={data.fare_rule} />;
  } else if (data.body) {
    content = <BodyBlock body={data.body} />;
  } else if (data.arrayData?.length) {
    content = <pre className="overflow-x-auto text-[11px] text-gray-700 md:text-xs">{toText(data.arrayData)}</pre>;
  }

  return (
    <div className="w-full lg:p-4">
      <div className="rounded-lg border bg-background p-3 md:p-4">
        {content || <Empty message={data.message} />}
        {!data.isLoader && data.additionalInfo ? (
          <div className="mt-4 border-t border-dashed border-gray-300 pt-3">
            <div className="mb-1 text-xs font-semibold text-gray-800">Additional Info</div>
            <div className="whitespace-pre-wrap text-[11px] leading-relaxed text-gray-600 md:text-xs">
              {toText(data.additionalInfo)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FareRule;
